// components/ProductivityTrendChart.jsx - FIXED VERSION
import React, { useState, useEffect, useMemo } from "react";
import "./ProductivityTrendChart.css";

const ProductivityTrendChart = ({ metrics, timeframe = "weekly" }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);
  
  // Debug logging - FIXED: Only log when metrics change
  useEffect(() => {
    console.log('📈 Productivity Metrics Received:', {
      hasMetrics: !!metrics,
      weeklyLength: metrics?.weekly?.length || 0,
      weeklyData: metrics?.weekly || []
    });
  }, [metrics]); // Only re-run when metrics change
  
  // Use useMemo to avoid recalculating on every render
  const chartData = useMemo(() => {
    if (metrics?.weekly?.length > 0) {
      return metrics.weekly;
    }
    
    // Fallback sample data for debugging
    const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    return days.map((day, index) => ({
      day,
      productivity: 70 + (Math.random() * 25),
      teamAvg: 65 + (Math.random() * 20)
    }));
  }, [metrics]); // Only recalculate when metrics change
  
  const hasData = metrics?.weekly?.length > 0;
  
  // Calculate stats using useMemo
  const stats = useMemo(() => {
    if (chartData.length === 0) {
      return {
        currentProductivity: 0,
        avgProductivity: 0,
        trend: 0,
        peakDay: 'N/A'
      };
    }
    
    const currentProductivity = chartData[chartData.length - 1]?.productivity || 0;
    const avgProductivity = Math.round(
      chartData.reduce((sum, d) => sum + (d.productivity || 0), 0) / chartData.length
    );
    
    // Calculate trend
    let trend = 0;
    if (chartData.length >= 2) {
      const last = chartData[chartData.length - 1].productivity || 0;
      const first = chartData[0].productivity || 0;
      trend = first > 0 ? ((last - first) / first * 100) : 0;
    }
    
    // Find peak day
    const peakEntry = chartData.reduce((max, d) => 
      (d.productivity || 0) > (max.productivity || 0) ? d : max, 
      chartData[0]
    );
    
    return {
      currentProductivity,
      avgProductivity,
      trend,
      peakDay: peakEntry?.day?.substring(0, 3) || 'N/A',
      hasRealData: hasData
    };
  }, [chartData, hasData]); // Only recalculate when chartData changes
  
  const maxProductivity = Math.max(
    ...chartData.map(d => d.productivity || 0),
    ...chartData.map(d => d.teamAvg || 0),
    100
  );

  return (
    <div className="productivity-chart">
      <div className="chart-header">
        
        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-dot individual"></span>
            <span>Team Avg</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot team"></span>
            <span>Overall Avg</span>
          </div>
        </div>
      </div>

      {!hasData && (
        <div className="data-warning">
        </div>
      )}

      <div className="chart-container">
        <div className="chart-y-axis">
          {[100, 75, 50, 25, 0].map(value => (
            <div key={value} className="y-label">{value}%</div>
          ))}
        </div>
        <div className="chart-bars">
          {chartData.map((data, index) => (
            <div key={index} className="bar-group">
              <div className="bar-container">
                <div 
                  className="bar team-bar" 
                  style={{ 
                    height: `${((data.teamAvg || 0) / maxProductivity) * 85}%`,
                    opacity: data.teamAvg ? 0.8 : 0.3
                  }}
                  title={`Team Avg: ${data.teamAvg || 0}%`}
                ></div>
                <div 
                  className="bar individual-bar" 
                  style={{ 
                    height: `${((data.productivity || 0) / maxProductivity) * 85}%`,
                    opacity: data.productivity ? 1 : 0.5
                  }}
                  title={`Productivity: ${data.productivity || 0}%`}
                ></div>
              </div>
              <div className="bar-label">{data.day?.substring(0, 3) || '---'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="chart-summary">
        
        <div className="summary-item">
          <div className="summary-label">Average</div>
          <div className="summary-value">
            {stats.avgProductivity > 0 ? `${stats.avgProductivity}%` : '--'}
          </div>
          <div className="summary-change">
            This week
          </div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Peak Day</div>
          <div className="summary-value">
            {stats.peakDay}
          </div>
          <div className="summary-change">
            Highest productivity
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductivityTrendChart;